import { useLoanApplicationEvents } from '@axo/shared/data-access/hooks';
import { useIsManager } from '@axo/shared/data-access/provider';
import { AceTable } from '../AceTable';
import {
  event_log,
  loan_application,
  permissions,
} from '@axo/shared/data-access/types';
import { AnchorButton, Divider, Tooltip } from '@blueprintjs/core';
import { formatDatetime } from '../../utils/formatDate';
import { useEffect, useMemo, useState } from 'react';
import { ITableConfig } from '../../models/tableConfig';
import { AceDropdown } from '../AceDropdown';
import { IconButton } from '../IconButton';
import classes from './LoanApplicationEventsCard.module.css';
import { Restricted } from '@axo/shared/feature/restricted';
import { AceCard } from '../AceCard';
import { UserName } from '../UserName';
import { SmallTag } from '../SmallTag';
import React from 'react';

const acmeUrl = import.meta.env.VITE_APP_ACME_URL;

function Origin({
  origin,
  marketCountry,
}: {
  origin: event_log.OriginIdentity;
  marketCountry: string;
}) {
  const content = origin.Roles.length
    ? origin.Roles.filter(
        (role) => !role.Country || role.Country === marketCountry,
      ).map((role) => (
        <SmallTag
          intent="none"
          minimal
          key={`${role.Name}/${role.Country}`}
          title={`"${role.Name}" role in ${
            role.Country ? `market "${role.Country}"` : 'all markets'
          }`}
        >
          {role.Name}
        </SmallTag>
      ))
    : '';

  return (
    <Restricted
      isAllowedFor={[permissions.RoleName.Manager]}
      fallback={content}
    >
      {origin.ClientID ? (
        <a
          href={`${acmeUrl}/clients/${origin.ClientID}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <SmallTag intent="none" minimal key={origin.ClientID}>
            <UserName clientID={origin.ClientID} />
          </SmallTag>
        </a>
      ) : (
        content
      )}
    </Restricted>
  );
}

function addDays(from: Date, numDays: number) {
  const to = new Date(from);
  to.setDate(to.getDate() + numDays);
  return to;
}

const beforeOptions = [
  { label: 'All', value: undefined },
  { label: '1 day ago', value: addDays(new Date(), -1) },
  { label: '3 days ago', value: addDays(new Date(), -3) },
  { label: '7 days ago', value: addDays(new Date(), -7) },
];

const sourceOptions = [
  { label: 'All', value: undefined },
  { label: 'Loan Application Service', value: 'loan-application' },
  { label: 'Customer Service', value: 'customer' },
  { label: 'Person Service', value: 'person' },
  { label: 'Loan Quote Service', value: 'loan-quote' },
  {
    label: 'Loan Quote Presentation Service',
    value: 'loan-quote-presentation',
  },
  { label: 'Payment Remark Service', value: 'payment-remark' },
  { label: 'Insurance', value: 'insurance' },
  { label: 'My Page', value: 'mypage' },
];

const mediumOptions = [
  { label: 'All', value: undefined },
  { label: 'Event Bridge', value: event_log.Medium.EventBridge },
  { label: 'Customer', value: event_log.Medium.Customer },
  { label: 'Client', value: event_log.Medium.Client },
];

export interface ILoanApplicationEvents {
  application: loan_application.LoanApplication;
}

export function LoanApplicationEventsCard({
  application,
}: ILoanApplicationEvents) {
  return (
    <AceCard
      id={`ApplicationHistory.${application.ID}`}
      title="Application history"
      noBorder
      isExpandedByDefault={false}
    >
      <LoanApplicationEventsContent application={application} />
    </AceCard>
  );
}

function LoanApplicationEventsContent({ application }: ILoanApplicationEvents) {
  const isManager = useIsManager();
  const [cursors, setCursors] = useState<string[]>([]);
  const [limit, setLimit] = useState(10);
  const [beforeDays, setBeforeDays] = useState<Date>();
  const [source, setSource] = useState<string>();
  const [medium, setMedium] = useState<event_log.Medium>();

  const cursor = cursors[cursors.length - 1];

  useEffect(() => {
    setCursors([]);
  }, [beforeDays]);

  const events = useLoanApplicationEvents({
    find: cursor
      ? undefined
      : {
          ApplicationContext: {
            LoanApplicationID: application.ID,
          },
          Source: source,
          Medium: medium,
          Before: beforeDays?.toISOString(),
          Limit: limit,
        },
    cursor,
  });

  const next = useMemo(() => {
    const nextCursor = events.data?.NextCursor;

    if (!nextCursor) return null;

    return () => setCursors((prev) => [...prev, nextCursor]);
  }, [events.data]);

  const prev = cursors.length
    ? () => setCursors((c) => c.slice(0, c.length - 2))
    : null;

  const managerColumns: ITableConfig<event_log.Event>['extra'] = [
    {
      label: 'Details',
      key: 'details',
      width: 'small',
      render(item) {
        return (
          <div>
            <AnchorButton
              small
              icon="document-share"
              minimal
              target="_blank"
              rel="noopener noreferrer"
              href={`${acmeUrl}/event-log/${item.ID}`}
              title="Open details in ACME"
            />
          </div>
        );
      },
    },
  ];

  return (
    <>
      <AceTable<event_log.Event>
        config={{
          columns: {
            EventCode: {
              label: 'Event Code',
              render: (_, item) => item.EventCode,
            },
            Medium: {
              width: 'medium',
              label: {
                label: 'Medium',
                content: (
                  <AceDropdown
                    label="Medium"
                    options={mediumOptions}
                    value={medium}
                    setValue={setMedium}
                  />
                ),
              },
              render: (_, item) => item.Medium,
            },
            Origin: {
              label: 'Origin',
              width: 'medium',
              render: (_, item) =>
                item.Origin ? (
                  <Origin
                    origin={item.Origin}
                    marketCountry={application.MarketCountry}
                  />
                ) : (
                  ''
                ),
            },
            Source: {
              width: 'medium',
              label: {
                label: 'Source',
                content: (
                  <AceDropdown
                    label="Source"
                    options={sourceOptions}
                    value={source}
                    setValue={setSource}
                  />
                ),
              },
              render: (_, item) => item.Source,
            },
            CreatedAt: {
              width: 'large',
              label: {
                label: 'Date of creation',
                content: (
                  <AceDropdown
                    label="Date of creation"
                    options={beforeOptions}
                    value={beforeDays}
                    setValue={setBeforeDays}
                  />
                ),
              },
              render: (_, item) => formatDatetime(item.CreatedAt),
            },
            Fields: {
              width: 'xlarge',
              label: 'Fields',
              render: (_, item) => {
                const fields = item.Fields.map((x) => `${x.Key}: ${x.Value}`);
                return (
                  <Tooltip
                    className={classes.fieldsTooltip}
                    content={
                      <>
                        {fields.map((field) => (
                          <React.Fragment key={field}>
                            <span>{field}</span>
                            <br />
                          </React.Fragment>
                        ))}
                      </>
                    }
                  >
                    {fields.join(', ')}
                  </Tooltip>
                );
              },
            },
          },
          extra: [...(isManager ? managerColumns : [])],
        }}
        data={events.data?.Items ?? []}
        loading={events.isLoading}
        uniqueRowIDKey="ID"
        maxRows={limit}
      />
      <div className={classes.footer}>
        <AceDropdown
          label="Rows per page"
          options={[5, 10, 20, 50, 100].map((x) => ({
            label: x.toString(),
            value: x,
          }))}
          value={limit}
          setValue={setLimit}
        />
        <Divider />
        <nav className={classes.nav}>
          <IconButton
            classes={{ container: classes.navButton }}
            icon="chevron-left"
            title="Prev"
            onClick={prev ?? undefined}
            disabled={!prev}
          />
          <IconButton
            classes={{ container: classes.navButton }}
            icon="chevron-right"
            title="Next"
            onClick={next ?? undefined}
            disabled={!next}
          />
        </nav>
      </div>
    </>
  );
}
